/**
 *  Plan store module file (Pinia)
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */
// The plan working-model is highly dynamic (server fields merged via spreads,
// nullable `package`, untyped sub-plan filters), so action payloads stay `any`,
// mirroring the original untyped JS. This is a follow-up strict-typing target.
import { defineStore } from 'pinia'
import { state, MODEL_PLAN, MODEL_ASSETS, MODEL_SUBPLAN_FILTERS } from './state'
import { getters as planGetters } from './getters'
import { rawClone } from '@/utils/clone'

import layerServiceC3 from '@/layers/layerService'
import { ASSETS, CLUSTER_ASSETS } from '@/layers'
import {
  deserializeFilters,
  deserializeLegend,
} from '@/plan/deserializeFilters'
import { filterAssetsFromPlan } from '@/helpers/plan.helper'
import PlanService from '@/services/plan.service'
import { getAppMetadata } from '@/plan/metadata/metadata'
import {
  getZoomLevelForDeclustering,
  getPackageExpireConfirmationTime,
  getPackageExpireCancelTime,
  isIntelligentMediaTypeDigitalStaticEnabled,
} from '@/plan/metadata/metadata.helper'
import { shouldAssetSimpleLayerBeVisible } from '@/helpers/map.helper'
import { getImpressionsForAssets } from '@/plan/filter/asset.helper'
import sharedPlanService from '@/services/sharedPlan.service'
import BrowserEnvironmentResolver from '@/helpers/environment.helper'
import { serializePlanPackage } from '@/utils/serializePackage'
import { serializeLegend } from '@/utils/serializeLegend'
import { getInstance as getAuth0ServiceIntance } from '@/auth/index'
import { isStateUpdateable } from '@/plan/filter/packages-browser.helper'
import { sendErrorMessageToUser } from '@/helpers/message/message.helper'
import AssetsService from '@/services/assets-browser.service'
import { Package } from '@workspaces/types'
import PackageService from '@/services/packages.service'
import {
  getExpirationForAgencyBookingConfirmation,
  getExpirationForAgencyCancellation,
} from '@/plan/filter/packages.helper'
import { sleep } from '@/helpers/common.helper'
import CommentsService from '@/services/comments.service'
import { analyzeAssetsForAllDigitalOrAllClassic } from '@/helpers/media-type.helper'
import { preparePackageSelection } from '@/helpers/package/package.helper'

export const MODULE_NAME = 'plan'

const APP_METADATA = getAppMetadata()

// Idiomatic Pinia option store: getters are pure `(state) => value` functions
// (see ./getters), and former Vuex mutations are folded into `this`-based actions
// alongside the async business actions.
export const usePlanStore = defineStore(MODULE_NAME, {
  state,
  getters: planGetters,
  actions: {
    // --- State setters (formerly Vuex mutations) ---
    savePlan(payload: any = {}) {
      this.plan = payload
      this.plan_backup = rawClone(this.plan)
    },
    savePlanFilter(payload: any = { filters: [], updateDirtyFlags: true }) {
      this.plan.filters = payload.filters
      this.plan.dirty = payload.updateDirtyFlags
        ? payload.updateDirtyFlags
        : true
      this.plan_backup = rawClone(this.plan)
    },
    modifyPlanSubfilterTabname(payload: any = {}) {
      this.plan.filters = [...this.plan.filters].map((item, index) => {
        if (index === payload.id) {
          item.tabname = payload.tabname
        }
        return item
      })
      this.plan_backup = rawClone(this.plan)
    },
    setPlanLoading(payload: any) {
      this.plan.loading = payload
    },
    setAssetsLoading(payload: any) {
      this.assets.loading = payload
    },
    setAssetsTotalStats(payload: any = {}) {
      this.assets.count = payload.count ?? -1
      if (!this.assets.meta.stats) {
        this.assets.meta.stats = {}
      }
      this.assets.meta.stats.impressions = payload.impressions ?? -1
      this.plan.impressions = payload.impressions ?? -1
    },
    setSubplanFiltersIndex(payload: any) {
      this.activeSubPlanFiltersIndex = payload
    },
    addSubplanFilters() {
      this.plan.filters = [...this.plan.filters, MODEL_SUBPLAN_FILTERS()]
      this.plan.filters[this.plan.filters.length - 1].countries = [
        ...this.plan.filters[0].countries,
      ]
      this.activeSubPlanFiltersIndex = this.plan.filters.length - 1
      this.plan.dirty = true
      this.plan_backup = rawClone(this.plan)
    },
    removeSubplanFilters(payload: any) {
      if (this.plan.filters.length > 1) {
        const filters = [...this.plan.filters].filter(
          (item, index) => index !== payload,
        )
        this.plan.filters = [...filters]
        this.plan.dirty = true
        this.plan_backup = rawClone(this.plan)
      }
    },
    resetPlan() {
      this.plan = MODEL_PLAN()
      this.assets = MODEL_ASSETS()
      this.isFeaturedPackageAdminDetail = false
      this.featuredPackageAdminDetailSourceTab = null
    },
    setFeaturedPackageAdminDetail(value: any) {
      this.isFeaturedPackageAdminDetail = value
    },
    setFeaturedPackageAdminDetailSourceTab(tab: any) {
      this.featuredPackageAdminDetailSourceTab = tab
    },
    setAssetsFinishedLoad(payload: any) {
      this.assetsFinishedLoad = payload
    },
    restorePlanFromBackup() {
      this.plan = rawClone(this.plan_backup)
    },
    setMapupdateflags(payload: any) {
      this.mapUpdateFlags = { ...this.mapUpdateFlags, ...payload }
    },
    updateLegendFilter(payload: any) {
      this.plan.legend = {
        ...this.plan.legend,
        ...payload,
      }
    },
    setAssetsDetails(payload: any) {
      this.assetsDetails = payload
    },
    setAssetsReadyWithAllProperties(payload: any) {
      this.assetsReadyWithAllProperties = payload.ready
    },
    setPackagesData(payload: any) {
      this.packages = payload
    },
    clearFilterPackages() {
      this.plan.package.filter.packages = []
    },
    clearPaneltypesPackages() {
      this.plan.package.filter.panelType.l1 = []
      this.plan.package.filter.panelType.l2 = []
      this.plan.package.filter.panelType.l3 = []
      this.plan.package.filter.panelType.l4 = []
    },
    setPackage({ package: planPackage, restoringBackup = false }: any) {
      let selectedPackagesIds = null
      if (planPackage.packages) {
        selectedPackagesIds = planPackage.packages.map(
          (packageSelected: any) => {
            return packageSelected.id
          },
        )
      }

      this.plan.package = planPackage
      this.plan.filters[this.activeSubPlanFiltersIndex].package =
        selectedPackagesIds
      if (!restoringBackup) {
        this.plan.dirty = true
      }
    },
    setPackagesPriceRange(payload: any) {
      this.plan.package.filter.priceRange = payload
      this.plan.dirty = true
    },
    updatePackagesFilter(payload: any) {
      this.plan.package.filter = { ...this.plan.package.filter, ...payload }
      this.plan.dirty = true
    },
    setPackagesDateRange(payload: any) {
      this.plan.package.filter.dateRange = payload
      this.plan.dirty = true
    },
    setAssetsFacingOrientationIcons(payload: any) {
      this.plan.legend.facingOrientationIcons = payload
    },
    addPackageAction(payload: any) {
      this.plan.package.actions.push(payload)
    },
    setAssetsIdsLayerVisibility(payload: any) {
      this.plan.legend.assetsIdsLayerVisibility = payload
    },
    setColorizeAssets(payload: any) {
      this.plan.legend.colorizeAssets = payload
    },
    setBasemap(payload: any) {
      this.plan.basemap = payload
    },
    setPackagePrice(payload: any) {
      this.plan.package.price = payload
      this.plan.dirty = true
    },
    setPackageBookedConfirmByAdminAt(payload: any) {
      this.plan.package.bookedConfirmByAdminAt = payload
    },
    setPackageBookedFinalizedAt(payload: any) {
      this.plan.package.bookedFinalizedAt = payload
    },
    setPackageExpirationForAgencyCancellation(payload: any) {
      this.plan.package.expirationForAgencyCancellation = payload
    },
    setPackageExpirationForAgencyBookingConfirmation(payload: any) {
      this.plan.package.expirationForAgencyBookingConfirmation = payload
    },
    setPackageState(payload: any) {
      this.plan.package.state = payload
    },
    setForceLogout(payload: any) {
      this.forceLogout = payload
    },
    setPackageAvailability(payload: any) {
      this.plan.package.availability = payload
    },
    setPlanName(payload: any) {
      this.plan.name = payload
    },
    setPlanDescription(payload: any) {
      this.plan.description = payload
    },
    setPlanClient(payload: any) {
      this.plan.client = payload
    },
    setPlanBrand(payload: any) {
      this.plan.brand = payload
    },
    setPlanVisibility(payload: any) {
      this.plan.is_public = payload
    },
    setHighlightedAssets(payload: any) {
      this.highlightedAssets = payload
    },
    setPlanDirty() {
      this.plan.dirty = true
    },
    setAssetsAngleNorthMap(payload: any) {
      this.plan.legend.angleNorthMap = payload
    },
    setAssetsDigitalStaticProperties(payload: any) {
      this.assetsProperties = payload
    },
    // Sync setter for plans.comments.data. Named `…Data` to coexist with the
    // async `setPlansComments` action below (the old store shadowed the mutation
    // of the same name with the action; this keeps both behaviours available).
    setPlansCommentsData(payload: any) {
      this.plans.comments.data = payload
    },
    setPlansCommentsLastUpdate(payload: any) {
      this.plans.comments.updatedAt = payload
    },
    setMyPlans(payload: any) {
      this.plans.user = payload
    },
    setPublicPlans(payload: any) {
      this.plans.public = payload
    },
    setPlansClients(payload: any) {
      this.plans.clients = payload
    },
    setIsSubscribedToComments(payload: any) {
      this.plans.comments.subscribed = payload
    },
    setLatestPlansCommentsUpdated(payload: any) {
      this.plans.comments.latest_plans_updated = payload
    },
    setIsSubscribedToPlans(payload: any) {
      this.plans.subscribed = payload
    },
    setEditingPlan(payload: any) {
      this.plan.editing = payload
    },

    // --- Business actions ---
    async loadPlan({
      id,
      sharedPlan,
      password,
    }: {
      id: string
      sharedPlan?: boolean
      password?: string
    }) {
      this.setPlanLoading(true)

      try {
        const defaultPlan = MODEL_PLAN()
        const defaultSubPlanFilters = MODEL_SUBPLAN_FILTERS()
        let plan: any = MODEL_PLAN()
        if (sharedPlan) {
          console.debug('🟪 Shared Plan: Fetching plan properties...')
          const planFetched = await sharedPlanService.getPlan(
            APP_METADATA,
            BrowserEnvironmentResolver.getInstance(),
            id,
            password,
          )

          plan = { ...plan, ...planFetched }

          if (typeof plan.filters === 'string') {
            plan.filters = deserializeFilters(plan.filters)
            plan.created_at = plan.created_at._seconds * 1000
            plan.modified_at = plan.modified_at?._seconds * 1000
          }
        } else {
          console.debug('🟪 Plan: Fetching...')
          plan = await PlanService.getPlan(
            APP_METADATA,
            BrowserEnvironmentResolver.getInstance(),
            id,
          )

          console.debug('🟪 Plan: Preparing filters...')
          const deserializedFilters = deserializeFilters(plan.filters)
          const filters = deserializedFilters.map((subfilter: any) => {
            return { ...defaultSubPlanFilters, ...subfilter }
          })
          plan.filters = filters
        }

        const preparedPlan = {
          ...defaultPlan,
          ...plan,
          dirty: false,
          created: plan.created_at,
          modified: plan.modified_at || plan.created_at,
          legend: deserializeLegend(plan.legend),
        }

        console.debug('🟪 Plan: Saving plan in the state...')
        this.savePlan(preparedPlan)
      } catch (err: any) {
        // eslint-disable-next-line
        console.error('[actions.js] Error in LOAD_PLAN', err)
        if (sharedPlan) {
          const currentUrl = window.location.href
          const parameters = currentUrl.split('?')[1]
          const sharedPlanFeedbackUrl = `/shared-plan-feedback?${parameters}&type=password&link=${currentUrl}`
          window.location.href = sharedPlanFeedbackUrl
        } else {
          const fromAPI = err.response && err.response.status >= 400
          window.alert(
            `${
              fromAPI ? 'API' : ''
            } Error loading plan. Check the browser console for more details`,
          )
        }
      } finally {
        this.setPlanLoading(false)
      }
    },
    async createPlan(payload: any) {
      const planFormatted = {
        ...payload,
        filters: JSON.stringify(payload.filters),
        package: serializePlanPackage(payload.package),
        legend: serializeLegend(payload.legend),
      }
      const id = await PlanService.createPlan(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        planFormatted,
      )
      const auth0Instance = getAuth0ServiceIntance()
      const userId = auth0Instance?.getUser()
      const plan = { ...payload, created_by: userId, id, modified: new Date() }
      this.savePlan({
        ...plan,
        dirty: false,
        filters: this.plan.filters,
        originFeaturedPackage: payload.originFeaturedPackage,
      })
      return plan
    },
    async updatePlan(payload: any) {
      const plan = {
        ...payload.plan,
        filters: JSON.stringify(payload.plan.filters),
        package: serializePlanPackage(payload.plan.package),
        legend: serializeLegend(payload.plan.legend),
      }

      await PlanService.updatePlan(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        payload.id,
        plan,
      )
      this.savePlan({
        ...plan,
        modified: new Date(),
        dirty: false,
        filters: this.plan.filters,
        package: payload.plan.package,
        originFeaturedPackage: payload.plan.originFeaturedPackage,
      })
    },
    async deletePlan({ id }: { id: string }) {
      return PlanService.deletePlan(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        id,
      )
    },
    async updateAssetLayer(params: any = {}) {
      let needToGetSecondaryPropertiesForAssets = false
      const plan = this.plan
      if (params.doNotEmitBusEvent === undefined) {
        params.doNotEmitBusEvent = false
      }

      const flyToExtent = params.flyToExtent ?? true

      this.setAssetsLoading(true)

      let allAssets: any[] = []

      const retiresForWaittingIndexDdInitialization = 5
      const timeBetweenRetries = [0, 100, 200, 300, 400]
      let retries = 0
      let successfullyFetchedAssets = false

      // Getting assets

      while (
        retries < retiresForWaittingIndexDdInitialization &&
        !successfullyFetchedAssets
      ) {
        await sleep(timeBetweenRetries[retries])
        try {
          console.debug(
            `🟪 Plan: 🔁 Fetching Assets: Attempt: ${retries + 1} || Assets: ${
              allAssets.length
            }`,
          )
          if (allAssets.length === 0) {
            const { allGeoJsonAssets, needToGetSecondaryProperties } =
              await AssetsService.fetchLayerV2(
                getAppMetadata(),
                BrowserEnvironmentResolver.getInstance(),
                plan.filters,
              )
            needToGetSecondaryPropertiesForAssets = needToGetSecondaryProperties
            console.debug(
              `🟪 Plan: 🐹 Secondary assets properties needed: ${needToGetSecondaryProperties}`,
            )
            allAssets = allGeoJsonAssets.data.features
            this.setAssetsReadyWithAllProperties({
              ready: !needToGetSecondaryProperties,
            })
          }
          successfullyFetchedAssets = true
        } catch (err) {
          console.error(
            '   🔴 Vuex - actions.js - UPDATE_ASSET_LAYER - An error occur while trying to fetch assets',
            err,
          )
          retries++
          successfullyFetchedAssets = false
        }
      }
      if (!successfullyFetchedAssets) {
        console.warn(
          '🔴 🔴 Could not set assets into indexBD, seems indexDB is not ready ... 🔴 🔴',
        )
        this.setMapupdateflags({ assets: false })
        this.setAssetsLoading(false)
        return
      }
      if (retries > 2) {
        console.warn(
          `🟠 Took ${
            retries + 1
          } attempts to correctly fetch assets, it would be a good idea to take a look why`,
        )
      }
      console.debug(
        `🟪 Plan: 🔁 Fetched Assets: Attempt: ${retries + 1} || Assets: ${
          allAssets.length
        }`,
      )

      const generateAuditEvent =
        params.generateAuditEvent === undefined
          ? true
          : params.generateAuditEvent
      console.debug('📢 Audit: Generating Event', generateAuditEvent)
      console.debug('🟪 Plan: 🔍 Filtering assets...')
      let assetsToShow = await filterAssetsFromPlan(
        allAssets,
        plan as any,
        generateAuditEvent,
      )

      // Update assets count and impressions
      const impressions = getImpressionsForAssets(assetsToShow)
      this.setAssetsTotalStats({
        count: assetsToShow.filter((asset: any) => !asset.properties.excluded)
          .length,
        impressions,
      })

      layerServiceC3.updateDeckInstance()

      const mapZoom = layerServiceC3.getZoom()
      const declusteringZoomLevel = getZoomLevelForDeclustering(APP_METADATA)
      const assetSimpleLayerShouldBeVisible = shouldAssetSimpleLayerBeVisible(
        declusteringZoomLevel,
        mapZoom,
      )
      const assetsLayerVisibility = assetsToShow.length > 0

      const secondaryPropertiesReady = this.assetsReadyWithAllProperties
      console.debug('       All assets properties are ready now? ', {
        needToGetSecondaryPropertiesForAssets,
        secondaryPropertiesReady,
      })
      if (needToGetSecondaryPropertiesForAssets && secondaryPropertiesReady) {
        const { allGeoJsonAssets } = await AssetsService.fetchLayerV2(
          getAppMetadata(),
          BrowserEnvironmentResolver.getInstance(),
          plan.filters,
        )
        allAssets = allGeoJsonAssets.data.features

        const assetsMap = new Map()
        allAssets.forEach((asset: any) => {
          assetsMap.set(asset.properties.id, asset)
        })
        const assetsToShowFullProperties = assetsToShow.map((asset: any) => {
          return assetsMap.get(asset.properties.id)
        })
        assetsToShow = assetsToShowFullProperties
        console.debug('        Re-fetched assets properties')
      }

      layerServiceC3.updateLayer(ASSETS, {
        ...params,
        data: allAssets,
        assetsToShow,
        visible: assetsLayerVisibility && assetSimpleLayerShouldBeVisible,
        flyToExtent,
        secondaryPropertiesReady,
      })
      layerServiceC3.updateLayer(CLUSTER_ASSETS, {
        ...params,
        data: allAssets,
        assetsToShow,
        visible: assetsLayerVisibility && !assetSimpleLayerShouldBeVisible,
        flyToExtent,
        secondaryPropertiesReady,
      })
      this.setMapupdateflags({ assets: false })
      this.setAssetsLoading(false)

      // When this redraw does not fly the camera (e.g. excluding/including
      // assets), deck.gl 9's idle AnimationLoop never draws a frame on its own,
      // so the layer changes above stay unrendered (stale icons). A flyTo drives
      // its own frames, so only force a redraw on the no-flyTo path. (sc-552667)
      if (!flyToExtent) {
        layerServiceC3.forceIdleRedraw()
      }

      if (isIntelligentMediaTypeDigitalStaticEnabled(APP_METADATA)) {
        const assetsProperties = analyzeAssetsForAllDigitalOrAllClassic(
          assetsToShow as any,
        )
        this.setAssetsDigitalStaticProperties(assetsProperties)
      }

      console.debug('🟢 Updating Assets Layer - OK')
    },
    async loadPackages() {
      const packages = await PackageService.getAllPackages(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
      )
      this.setPackagesData(packages)
    },
    updateAssetsFacingOrientationIcons(enabled: any) {
      this.setAssetsFacingOrientationIcons(enabled)
    },
    updateAssetsIdsLayerVisibility(enabled: any) {
      this.setAssetsIdsLayerVisibility(enabled)
    },
    updateColorizeAssets(enabled: any) {
      this.setColorizeAssets(enabled)
    },
    async removePackageSelection(packageId: any) {
      const allPackages = this.packages
      const packageFilter = this.plan.package
      packageFilter.packages = packageFilter.packages.filter(
        (packageSelected: any) => packageSelected.id !== packageId,
      )
      const updatedFilter = preparePackageSelection(
        [],
        allPackages,
        packageFilter,
      )

      this.setPackage({ package: updatedFilter })
    },
    setPackageSelection(payload: any) {
      const allPackages = this.packages
      const currentPackageSelection = this.plan.package
      const newPackageSelection = payload

      const updatedPackageSelection = preparePackageSelection(
        newPackageSelection,
        allPackages,
        currentPackageSelection,
      )

      this.setPackage({
        package: updatedPackageSelection,
      })
    },
    async updatePackageState(payload: any) {
      const { state: stateToUpdate } = payload
      const plan = this.plan
      const currentPlan = await PlanService.getPlan(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        plan.id,
      )
      const currentPackageState = (currentPlan.package as any).state
      const packageState = plan.package.state

      if (packageState !== currentPackageState) {
        sendErrorMessageToUser({
          titleLocaleKey: 'ERROR_PLAN_UPDATE_STATE_TITLE',
          msgLocaleKey: 'ERROR_PLAN_UPDATE_LATEST_VERSION',
        })
        throw new Error(
          'The package state cannot be updated because user is not using current version of the plan',
        )
      }

      let finalStateToUpdate = stateToUpdate

      if (!isStateUpdateable(packageState, stateToUpdate, plan as any)) {
        sendErrorMessageToUser({
          titleLocaleKey: 'ERROR_PLAN_UPDATE_STATE_TITLE',
          msgLocaleKey: 'ERROR_PLAN_UPDATE_STATE_DESCRIPTION',
          title: 'Error updating package state',
          msg: 'The package state cannot be updated',
        })
        throw new Error(
          `The package state cannot be updated because state cannot be updated from ${packageState} to ${stateToUpdate}`,
        )
      }

      const auth0Instance = getAuth0ServiceIntance()
      const userId = auth0Instance?.getUser()
      const operationTimestamp = new Date()
      plan.package.actions.push({
        state: stateToUpdate,
        timestamp: operationTimestamp,
        user: userId,
      })

      switch (stateToUpdate) {
        case Package.State.BookingRequest:
          plan.package.bookedConfirmByAdminAt = undefined
          plan.package.expirationForAgencyBookingConfirmation = undefined
          break
        case Package.State.BookingConfirmedByAdmin:
          plan.package.bookedConfirmByAdminAt = operationTimestamp
          plan.package.expirationForAgencyBookingConfirmation =
            getExpirationForAgencyBookingConfirmation(
              operationTimestamp,
              getPackageExpireConfirmationTime(APP_METADATA),
            )
          break
        case Package.State.ReBooking:
          plan.package.bookedConfirmByAdminAt = undefined
          plan.package.expirationForAgencyBookingConfirmation = undefined
          finalStateToUpdate = Package.State.BookingRequest
          break
        case Package.State.Booked:
          plan.package.bookedFinalizedAt = operationTimestamp
          plan.package.expirationForAgencyCancellation =
            getExpirationForAgencyCancellation(
              plan.package.filter.dateRange[0],
              getPackageExpireCancelTime(APP_METADATA),
            )
          break
      }

      // Update package state on state
      this.setPackageState(finalStateToUpdate)
      // Update package on Firestore
      const payloadForUpdate = {
        plan,
        id: plan.id,
      }
      await this.updatePlan(payloadForUpdate)

      // Send email
    },
    async formEditingUpdatePackageState(payload: any) {
      const { state: stateToUpdate } = payload
      const plan = this.plan
      const auth0Instance = getAuth0ServiceIntance()
      const userId = auth0Instance?.getUser()
      const operationTimestamp = new Date()
      const expirationTime = getExpirationForAgencyBookingConfirmation(
        operationTimestamp,
        getPackageExpireConfirmationTime(APP_METADATA),
      )
      const expirationAgencyCancellationTime =
        getExpirationForAgencyCancellation(
          plan.package.filter.dateRange[0],
          getPackageExpireCancelTime(APP_METADATA),
        )

      let finalStateToUpdate = stateToUpdate

      // Add action to package
      this.addPackageAction({
        state: stateToUpdate,
        timestamp: operationTimestamp,
        user: userId,
      })
      // Modify package parameters related to the new state
      switch (stateToUpdate) {
        case Package.State.BookingRequest:
          this.setPackageBookedConfirmByAdminAt(undefined)
          this.setPackageExpirationForAgencyBookingConfirmation(undefined)
          break
        case Package.State.BookingConfirmedByAdmin:
          this.setPackageBookedConfirmByAdminAt(operationTimestamp)
          this.setPackageExpirationForAgencyBookingConfirmation(expirationTime)
          break
        case Package.State.ReBooking:
          this.setPackageBookedConfirmByAdminAt(undefined)
          this.setPackageExpirationForAgencyBookingConfirmation(undefined)
          finalStateToUpdate = Package.State.BookingRequest
          break
        case Package.State.Booked:
          this.setPackageBookedFinalizedAt(operationTimestamp)
          this.setPackageExpirationForAgencyCancellation(
            expirationAgencyCancellationTime,
          )
          break
      }
      // Update package state on state
      this.setPackageState(finalStateToUpdate)
    },
    async updatePackageAvailability(payload: any) {
      const plan = this.plan
      if (!plan.package) {
        return
      }
      const { availability: availabilityToUpdate } = payload

      // Update package state on state
      this.setPackageAvailability(availabilityToUpdate)
    },
    async setPlansComments(payload: any) {
      const plansComments = await CommentsService.getAllNewComments(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        payload,
      )
      this.setPlansCommentsData(plansComments)
    },
    async markPlanCommentsRead(payload: any) {
      await CommentsService.markAllPlanCommentsAsRead(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        payload.planId,
      )
      const plansComments = this.plans.comments.data
      const filteredPlanComments = plansComments.filter((comment: any) => {
        return comment.planId !== payload.planId
      })
      this.setPlansCommentsData(filteredPlanComments)
    },
    async markAllPlansCommentsRead(payload: any) {
      payload.planIds.forEach(async (planId: any) => {
        await CommentsService.markAllPlanCommentsAsRead(
          APP_METADATA,
          BrowserEnvironmentResolver.getInstance(),
          planId,
        )
      })
      this.setPlansCommentsData([])
    },
  },
})
